import { c as _c } from "react/compiler-runtime";
import { DesignPrintSummary } from '@/components/design/steps/DesignPrintSummary';
import { PrintHeader } from '@/components/print/PrintHeader';
import { useTranslation } from '@/hooks/use-translation';
import { formatSignedWeightValue, formatWeightValue } from '@/lib/format/format-weight';
import { KeyColor } from '@/lib/piano/keyboard';
import { ADJUSTMENT_THRESHOLD } from '@/lib/piano/touch-design/bench-adjustment';
import { type AdjustmentSheetProps } from './AdjustmentSheet.types';
import { AdjustmentSheetTableRow } from './AdjustmentSheetTableRow';
import { useAdjustmentRows } from './hooks/use-adjustment-rows';
const SummaryTile = t0 => {
  const $ = _c(10);
  const {
    detail,
    figure,
    label
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-[13px] text-muted-foreground">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== figure) {
    t2 = <span className="font-serif text-[26px] leading-[1.1] font-medium">{figure}</span>;
    $[2] = figure;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== detail) {
    t3 = <span className="font-mono text-[12.5px] text-muted-foreground">{detail}</span>;
    $[4] = detail;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className="flex flex-[1_1_220px] flex-col gap-1.5 rounded-md border border-border bg-card px-[18px] py-4">{t1}{t2}{t3}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};

/**
 * The sheet that goes to the bench: for every key, what it reads now, what it
 * should read, and the weight to add or take off.
 */
export const AdjustmentSheet: React.FC<AdjustmentSheetProps> = t0 => {
  const $ = _c(140);
  const {
    analyzedKeyboard,
    designedKeyboard
  } = t0;
  const {
    currentLanguage,
    t
  } = useTranslation();
  const {
    keyboard,
    summary
  } = useAdjustmentRows(analyzedKeyboard, designedKeyboard);
  let t1;
  if ($[0] !== t) {
    t1 = t("Adjustment Sheet", {
      ns: "navigation"
    });
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <PrintHeader title={t1} />;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DesignPrintSummary />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("Front Weight", {
      ns: "navigation"
    });
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== summary.frontWeightCount || $[8] !== t) {
    t5 = t("{{count}} keys", {
      count: summary.frontWeightCount,
      ns: "navigation"
    });
    $[7] = summary.frontWeightCount;
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== currentLanguage || $[11] !== summary.frontWeightMean || $[12] !== t) {
    t6 = summary.frontWeightMean === null ? t("nothing to adjust") : t("{{weight}} g on average", {
      weight: formatSignedWeightValue(summary.frontWeightMean, currentLanguage)
    });
    $[10] = currentLanguage;
    $[11] = summary.frontWeightMean;
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t4 || $[15] !== t5 || $[16] !== t6) {
    t7 = <SummaryTile label={t4} figure={t5} detail={t6} />;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t) {
    t8 = t("Strike Weight", {
      ns: "navigation"
    });
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== summary.strikeWeightCount || $[21] !== t) {
    t9 = t("{{count}} hammers", {
      count: summary.strikeWeightCount
    });
    $[20] = summary.strikeWeightCount;
    $[21] = t;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== currentLanguage || $[24] !== summary.strikeWeightMax || $[25] !== summary.strikeWeightMin || $[26] !== t) {
    t10 = summary.strikeWeightMin === null || summary.strikeWeightMax === null ? null : t("from {{min}} to {{max}} g", {
      max: formatSignedWeightValue(summary.strikeWeightMax, currentLanguage),
      min: formatSignedWeightValue(summary.strikeWeightMin, currentLanguage)
    });
    $[23] = currentLanguage;
    $[24] = summary.strikeWeightMax;
    $[25] = summary.strikeWeightMin;
    $[26] = t;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== summary.strikeWeightUnchanged || $[29] !== t) {
    t11 = t("{{count}} unchanged", {
      count: summary.strikeWeightUnchanged
    });
    $[28] = summary.strikeWeightUnchanged;
    $[29] = t;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t10 || $[32] !== t11) {
    t12 = [t10, t11].filter(Boolean);
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const t13 = t12.join(" \xB7 ");
  let t14;
  if ($[34] !== t13 || $[35] !== t8 || $[36] !== t9) {
    t14 = <SummaryTile label={t8} figure={t9} detail={t13} />;
    $[34] = t13;
    $[35] = t8;
    $[36] = t9;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== summary.hasSprings || $[39] !== summary.springCount || $[40] !== summary.springFirstKey || $[41] !== summary.springLastKey || $[42] !== t) {
    t15 = summary.hasSprings ? <SummaryTile label={t("Wippen Support Springs", {
      ns: "navigation"
    })} figure={t("{{count}} springs", {
      count: summary.springCount
    })} detail={t("keys {{first}} to {{last}}", {
      first: summary.springFirstKey ?? 0,
      last: summary.springLastKey ?? 0
    })} /> : null;
    $[38] = summary.hasSprings;
    $[39] = summary.springCount;
    $[40] = summary.springFirstKey;
    $[41] = summary.springLastKey;
    $[42] = t;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== t14 || $[45] !== t15 || $[46] !== t7) {
    t16 = <div className="flex flex-wrap gap-3 print:hidden">{t7}{t14}{t15}</div>;
    $[44] = t14;
    $[45] = t15;
    $[46] = t7;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  let t18;
  let t19;
  let t20;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <col className="w-[52px]" />;
    t18 = <col className="w-[64px]" />;
    t19 = <col />;
    t20 = <col />;
    $[48] = t17;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
  } else {
    t17 = $[48];
    t18 = $[49];
    t19 = $[50];
    t20 = $[51];
  }
  let t21;
  if ($[52] !== summary.hasSprings) {
    t21 = summary.hasSprings ? <col /> : null;
    $[52] = summary.hasSprings;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <col className="ptl-print-only w-[64px]" />;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== t21) {
    t23 = <colgroup>{t17}{t18}{t19}{t20}{t21}{t22}</colgroup>;
    $[55] = t21;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== t) {
    t24 = t("No.");
    $[57] = t;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== t24) {
    t25 = <th className="h-12 border-b border-ptl-rule-strong bg-card px-3 text-left align-middle text-[13px] font-semibold text-ptl-ink-3">{t24}</th>;
    $[59] = t24;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== t) {
    t26 = t("Note");
    $[61] = t;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== t26) {
    t27 = <th className="h-12 border-b border-ptl-rule-strong bg-card px-3 text-left align-middle text-[13px] font-semibold text-ptl-ink-3">{t26}</th>;
    $[63] = t26;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  let t28;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <span className="mr-2 font-mono text-[11.5px] font-medium text-ptl-brass">FW</span>;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== t) {
    t29 = t("Front Weight", {
      ns: "navigation"
    });
    $[66] = t;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== t29) {
    t30 = <th className="h-12 border-b border-b-ptl-rule-strong bg-card px-3 text-left align-middle text-[13px] font-semibold print:border-l">{t28}{t29}</th>;
    $[68] = t29;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <span className="mr-2 font-mono text-[11.5px] font-medium text-ptl-brass">SW</span>;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  let t32;
  if ($[71] !== t) {
    t32 = t("Strike Weight", {
      ns: "navigation"
    });
    $[71] = t;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] !== t32) {
    t33 = <th className="h-12 border-b border-b-ptl-rule-strong bg-card px-3 text-left align-middle text-[13px] font-semibold print:border-l">{t31}{t32}</th>;
    $[73] = t32;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  let t34;
  if ($[75] !== summary.hasSprings || $[76] !== t) {
    t34 = summary.hasSprings ? <th className="h-12 border-b border-b-ptl-rule-strong bg-card px-3 text-left align-middle text-[13px] font-semibold print:border-l"><span className="mr-2 font-mono text-[11.5px] font-medium text-ptl-brass">BWS</span>{t("Wippen Support Springs", {
        ns: "navigation"
      })}</th> : null;
    $[75] = summary.hasSprings;
    $[76] = t;
    $[77] = t34;
  } else {
    t34 = $[77];
  }
  let t35;
  if ($[78] !== t) {
    t35 = t("Done");
    $[78] = t;
    $[79] = t35;
  } else {
    t35 = $[79];
  }
  let t36;
  if ($[80] !== t35) {
    t36 = <th className="ptl-print-only h-12 border-b border-b-ptl-rule-strong border-l border-l-border bg-card px-3 text-center align-middle text-[13px] font-semibold">{t35}</th>;
    $[80] = t35;
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  let t37;
  if ($[82] !== t) {
    t37 = t("Notes");
    $[82] = t;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  let t38;
  if ($[84] !== t37) {
    t38 = <th className="ptl-print-only h-12 border-b border-b-ptl-rule-strong border-l border-l-border px-3 text-left align-middle text-[13px] font-semibold">{t37}</th>;
    $[84] = t37;
    $[85] = t38;
  } else {
    t38 = $[85];
  }
  let t39;
  if ($[86] !== t25 || $[87] !== t27 || $[88] !== t30 || $[89] !== t33 || $[90] !== t34 || $[91] !== t36 || $[92] !== t38) {
    t39 = <thead><tr>{t25}{t27}{t30}{t33}{t34}{t36}{t38}</tr></thead>;
    $[86] = t25;
    $[87] = t27;
    $[88] = t30;
    $[89] = t33;
    $[90] = t34;
    $[91] = t36;
    $[92] = t38;
    $[93] = t39;
  } else {
    t39 = $[93];
  }
  let t40;
  if ($[94] !== keyboard || $[95] !== summary.hasSprings) {
    let t41;
    if ($[97] !== summary.hasSprings) {
      t41 = key => <AdjustmentSheetTableRow key={key.number} hasWippenAssistSpringDesign={summary.hasSprings} isBlackKey={key.color === KeyColor.Black} item={key} />;
      $[97] = summary.hasSprings;
      $[98] = t41;
    } else {
      t41 = $[98];
    }
    t40 = keyboard.mapToArray(t41);
    $[94] = keyboard;
    $[95] = summary.hasSprings;
    $[96] = t40;
  } else {
    t40 = $[96];
  }
  let t41;
  if ($[99] !== t40) {
    t41 = <tbody>{t40}</tbody>;
    $[99] = t40;
    $[100] = t41;
  } else {
    t41 = $[100];
  }
  let t42;
  if ($[101] !== t23 || $[102] !== t39 || $[103] !== t41) {
    t42 = <div className="overflow-x-auto print:overflow-visible"><table className="w-full min-w-[820px] table-fixed border-collapse print:table-auto print:min-w-0">{t23}{t39}{t41}</table></div>;
    $[101] = t23;
    $[102] = t39;
    $[103] = t41;
    $[104] = t42;
  } else {
    t42 = $[104];
  }
  let t43;
  if ($[105] !== keyboard.size || $[106] !== t) {
    t43 = t("{{count}} keys", {
      count: keyboard.size,
      ns: "navigation"
    });
    $[105] = keyboard.size;
    $[106] = t;
    $[107] = t43;
  } else {
    t43 = $[107];
  }
  let t44;
  if ($[108] !== t43) {
    t44 = <span className="font-mono text-xs">{t43}</span>;
    $[108] = t43;
    $[109] = t44;
  } else {
    t44 = $[109];
  }
  let t45;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <span className="rounded-full bg-ptl-delta-plus px-[7px] py-0.5 text-xs text-ptl-delta-plus-foreground">+</span>;
    $[110] = t45;
  } else {
    t45 = $[110];
  }
  let t46;
  if ($[111] !== t) {
    t46 = t("to add");
    $[111] = t;
    $[112] = t46;
  } else {
    t46 = $[112];
  }
  let t47;
  if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <span className="rounded-full bg-ptl-delta-minus px-[7px] py-0.5 text-xs text-ptl-delta-minus-foreground">−</span>;
    $[113] = t47;
  } else {
    t47 = $[113];
  }
  let t48;
  if ($[114] !== t) {
    t48 = t("to remove");
    $[114] = t;
    $[115] = t48;
  } else {
    t48 = $[115];
  }
  let t49;
  if ($[116] !== t) {
    t49 = t("values in grams");
    $[116] = t;
    $[117] = t49;
  } else {
    t49 = $[117];
  }
  let t50;
  if ($[118] !== t49) {
    t50 = <span className="text-ptl-ink-3">{t49}</span>;
    $[118] = t49;
    $[119] = t50;
  } else {
    t50 = $[119];
  }
  let t51;
  if ($[120] !== t46 || $[121] !== t48 || $[122] !== t50) {
    t51 = <span className="inline-flex flex-wrap items-center gap-3">{t45}{t46}{t47}{t48}{t50}</span>;
    $[120] = t46;
    $[121] = t48;
    $[122] = t50;
    $[123] = t51;
  } else {
    t51 = $[123];
  }
  let t52;
  if ($[124] !== t44 || $[125] !== t51) {
    t52 = <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-2 border-t border-border px-4 py-3.5 text-[13px] text-muted-foreground print:hidden">{t44}{t51}</div>;
    $[124] = t44;
    $[125] = t51;
    $[126] = t52;
  } else {
    t52 = $[126];
  }
  let t53;
  if ($[127] !== t42 || $[128] !== t52) {
    t53 = <section className="overflow-hidden rounded-md border border-ptl-rule-strong bg-card print:overflow-visible print:rounded-none print:border-0">{t42}{t52}</section>;
    $[127] = t42;
    $[128] = t52;
    $[129] = t53;
  } else {
    t53 = $[129];
  }
  let t54;
  if ($[130] !== currentLanguage || $[131] !== t) {
    t54 = t("Values in grams: current \u2192 target, then the difference to apply (+ to add, \u2212 to remove). \u201Cunchanged\u201D: a difference below {{threshold}} g.", {
      threshold: formatWeightValue(ADJUSTMENT_THRESHOLD, currentLanguage)
    });
    $[130] = currentLanguage;
    $[131] = t;
    $[132] = t54;
  } else {
    t54 = $[132];
  }
  let t55;
  if ($[133] !== t54) {
    t55 = <p className="ptl-print-only m-0 text-[12px] leading-[1.5] text-muted-foreground">{t54}</p>;
    $[133] = t54;
    $[134] = t55;
  } else {
    t55 = $[134];
  }
  let t56;
  if ($[135] !== t16 || $[136] !== t2 || $[137] !== t53 || $[138] !== t55) {
    t56 = <div className="flex flex-col gap-6">{t2}{t3}{t16}{t53}{t55}</div>;
    $[135] = t16;
    $[136] = t2;
    $[137] = t53;
    $[138] = t55;
    $[139] = t56;
  } else {
    t56 = $[139];
  }
  return t56;
};